// CatalogAdoptService.js
//
// Ensure a workspace node exists for a catalog entry (idempotent adopt).

import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';
import { createNode as createNodeDoc } from 'services/nodes/NodeWriteService';
import { createNode as createNodeFromType } from 'utilities/NodeFactory';
import { getTypeCatalogEntry } from 'services/catalog/TypeCatalogService';
import { applyCatalogLiveRead, getNodeCatalogId } from 'utilities/PlatformCatalog';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * Find an existing adopted workspace node for a catalog entry.
 *
 * @param {{ baseKey: string, typeKey: string, catalogId: string }} params
 * @returns {Promise<object|null>}
 */
export async function findAdoptedCatalogNode({ baseKey, typeKey, catalogId } = {}) {
  const cleanedBaseKey = normalizeString(baseKey);
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedCatalogId = normalizeString(catalogId);
  if (!cleanedBaseKey || !cleanedTypeKey || !cleanedCatalogId) return null;

  const constraints = [
    where('baseKey', '==', cleanedBaseKey),
    where('type', '==', cleanedTypeKey),
    where('catalogId', '==', cleanedCatalogId),
    limit(5),
  ];

  const snapshot = await getDocs(query(collection(db, collections.nodes), ...constraints));
  const rows = snapshot.docs
    .map((docSnap) => ({ key: docSnap.id, ...(docSnap.data() || {}) }))
    .filter((node) => !node?.deleted && node?.status !== 'deleted' && !node?.dateDeleted);

  return rows[0] || null;
}

/**
 * Adopt a catalog entry into the workspace (create pointer node if needed).
 *
 * @param {object} params
 * @param {string} params.typeKey
 * @param {string} params.catalogId
 * @param {object} params.user
 * @param {string} params.baseKey
 * @param {object|null} [params.catalogEntry]
 * @param {object|null} [params.typeDef]
 * @param {string} [params.module]
 * @returns {Promise<object>} Workspace node (adopted)
 */
export async function adoptCatalogEntry({
  typeKey,
  catalogId,
  user,
  baseKey,
  catalogEntry: catalogEntryProp = null,
  typeDef = null,
  module: nodeModule = 'private',
} = {}) {
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedCatalogId = normalizeString(catalogId);
  const cleanedBaseKey = normalizeString(baseKey);
  if (!cleanedTypeKey || !cleanedCatalogId || !cleanedBaseKey) {
    throw new Error('typeKey, catalogId, and baseKey are required');
  }
  if (!user) throw new Error('user is required');

  const existing = await findAdoptedCatalogNode({
    baseKey: cleanedBaseKey,
    typeKey: cleanedTypeKey,
    catalogId: cleanedCatalogId,
  });
  if (existing) {
    const entry = catalogEntryProp || await getTypeCatalogEntry(cleanedTypeKey, cleanedCatalogId);
    return applyCatalogLiveRead(existing, entry, typeDef) || existing;
  }

  const catalogEntry = catalogEntryProp
    || await getTypeCatalogEntry(cleanedTypeKey, cleanedCatalogId);
  if (!catalogEntry) {
    throw new Error(`Catalog entry not found: ${cleanedTypeKey}/${cleanedCatalogId}`);
  }

  const node = await createNodeFromType(cleanedTypeKey, user, cleanedBaseKey, {
    module: nodeModule,
  });
  node.catalogId = cleanedCatalogId;
  // Cache title for indexes/lists; live-read remains source of truth for catalog fields.
  node.title = normalizeString(catalogEntry.title) || node.title || '';

  await createNodeDoc(node.key, node);

  return applyCatalogLiveRead(node, catalogEntry, typeDef) || node;
}

/**
 * Resolve a picker value that may be a catalog option or an existing node key.
 *
 * @param {object} params
 * @param {string} params.value
 * @param {string} params.targetType
 * @param {object} params.user
 * @param {string} params.baseKey
 * @param {Map<string, object>|object[]} [params.catalogById]
 * @param {object|null} [params.typeDef]
 * @returns {Promise<string>} Workspace node key
 */
export async function resolveRelationTargetToNodeKey({
  value,
  targetType,
  user,
  baseKey,
  catalogById = null,
  typeDef = null,
} = {}) {
  const raw = normalizeString(value);
  if (!raw) return '';

  if (raw.startsWith('__catalog__')) {
    const catalogId = raw.slice('__catalog__'.length).trim();
    let entry = null;
    if (catalogById instanceof Map) {
      entry = catalogById.get(catalogId) || null;
    } else if (catalogById && typeof catalogById === 'object') {
      entry = catalogById[catalogId] || null;
    }
    const adopted = await adoptCatalogEntry({
      typeKey: targetType,
      catalogId,
      user,
      baseKey,
      catalogEntry: entry,
      typeDef,
    });
    return normalizeString(adopted?.key);
  }

  return raw;
}

/**
 * @param {object|null|undefined} node
 * @returns {boolean}
 */
export function nodeHasCatalogLink(node) {
  return Boolean(getNodeCatalogId(node));
}
